import axios, { AxiosRequestConfig } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { Props as InfiniteScrollProps } from 'react-infinite-scroll-component';
import { scrollableContentClassName } from 'app/components/ScrollableContent';
import { useAppDispatch, useAppSelector } from 'app/store';
import { Link } from 'types/api.types';
import { UseFetch, UseInfiniteLoaderProps } from './types';
import { useIsMounted } from './state';

export const useFetch = <T extends {}, R = { success: boolean } | undefined>(
  configOptions?: AxiosRequestConfig
): UseFetch<T, R> => {
  const isMounted = useIsMounted();
  const [state, setState] = useState({
    error: null,
    isLoading: false,
    done: false,
    data: null,
  });

  const [config, setConfig] = useState(configOptions);

  useEffect(() => {
    if (!isEqual(config, configOptions)) {
      setConfig(configOptions);
    }
  }, [config, configOptions]);

  const callAPI = useCallback(
    async (url: string, payload?: T) => {
      setState((prevState) => ({ ...prevState, isLoading: true, done: false }));
      try {
        let response;

        if (config?.method) {
          response = await axios({
            url,
            withCredentials: true,
            ...config,
            data: { ...config.data, ...payload },
          });
        } else {
          response = payload
            ? await axios.post(url, payload, {
                withCredentials: true,
                ...config,
              })
            : await axios.get(url, { withCredentials: true, ...config });
        }
        const { data } = response;
        if (isMounted()) {
          setState({ isLoading: false, done: true, error: null, data });
        }
        return data;
      } catch (e: any) {
        if (isMounted()) {
          setState({
            isLoading: false,
            done: true,
            error: e.response,
            data: null,
          });
        }
        if (e.isAxiosError) {
          throw e;
        }
      }
      return undefined;
    },
    [isMounted, config]
  );
  return {
    callAPI,
    isLoading: state.isLoading,
    done: state.done,
    error: state.error,
    data: state.data,
  };
};

export const useInfiniteLoader: (
  props: UseInfiniteLoaderProps
) => [string | undefined, InfiniteScrollProps, boolean] = ({
  listId,
  LoaderComponent,
  nextLinkSelector,
  fetchNext,
  dataLength,
}) => {
  const [fetchingMore, setFetchingMore] = useState(false);
  const scrollableContainerEl = document.querySelector(
    `.${scrollableContentClassName}`
  );
  const nextLink = useAppSelector(nextLinkSelector);
  const dispatch = useAppDispatch();
  const fetchMore = async () => {
    setFetchingMore(true);
    if (!nextLink) {
      setFetchingMore(false);
      return null;
    }
    const response = await dispatch(fetchNext(nextLink)).unwrap();
    setFetchingMore(false);
    return response;
  };

  return [
    listId,
    {
      scrollableTarget: listId || scrollableContainerEl,
      next: fetchMore,
      hasMore: Boolean(nextLink),
      loader: LoaderComponent,
      dataLength,
    } as InfiniteScrollProps,
    fetchingMore,
  ];
};

export const useFetchValidationSchema = <Schema = any>({
  link,
  initialLoading,
}: {
  link?: Link;
  initialLoading?: boolean;
}) => {
  const isMounted = useIsMounted();
  const [fetchingValidations, setFetchingValidations] = useState(
    initialLoading || Boolean(link)
  );
  const [validationSchemaFetched, setValidationSchemaFetched] = useState(false);
  const [validationSchema, setValidationSchema] = useState<Schema>();

  const fetchValidationSchema = useCallback(
    async (config: AxiosRequestConfig) => {
      if (!isMounted()) return;
      try {
        setFetchingValidations(true);
        const response = await axios.request({
          url: config.url,
          method: config?.method,
          headers: { Accept: 'application/json+schema' },
          withCredentials: true,
        });
        if (!isMounted()) {
          return;
        }
        setValidationSchema(response?.data?.response?.schema?.properties?.body);
        setValidationSchemaFetched(true);
        setFetchingValidations(false);
      } catch (e) {
        if (!isMounted()) {
          return;
        }
        setValidationSchemaFetched(false);
        setFetchingValidations(false);
      }
    },
    [isMounted]
  );

  useEffect(() => {
    if (link?.href && !validationSchemaFetched) {
      fetchValidationSchema({ url: link.href, method: link.method });
    }
  }, [link, fetchValidationSchema, validationSchemaFetched]);

  return {
    validationSchema,
    fetchingValidations,
    validationSchemaFetched,
  };
};
